// Plan 068 Day 4 — Bulk regenerate FAQ audio dialog.
// Filter options: missing (default) / stale / failed / all (with confirm).
// Default = 'missing' so admin can't accidentally re-synth working audio in
// one click. Each option shows the count of FAQs it would regenerate so the
// blast radius is visible before submit.
//
// Status updates per-FAQ arrive as 'faq_audio_status' socket events handled
// in FaqPage.js — this dialog is fire-and-close.

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import faqService from '../services/faqService';

const NEUTRAL_CHIP_SX = {
  height: 22,
  fontSize: '12px',
  bgcolor: '#f5f5f5',
  color: 'rgba(0,0,0,0.72)',
  fontWeight: 500,
};

const FILTER_OPTIONS = [
  {
    value: 'missing',
    label: '產生所有未就緒的語音（建議）',
    helper: '包含 失敗 / 需更新 / 從未產生 過的項目，已就緒的不會重做',
    count: (s) => (s.missing || 0) + (s.failed || 0) + (s.stale || 0),
  },
  {
    value: 'stale',
    label: '只重新產生「需更新」的 FAQ',
    helper: '回答內容變更後尚未重新合成語音的項目',
    count: (s) => s.stale || 0,
  },
  {
    value: 'failed',
    label: '只重新產生「失敗」的 FAQ',
    helper: '上次語音合成失敗的項目',
    count: (s) => s.failed || 0,
  },
  {
    value: 'all',
    label: '重新產生所有語音（會覆蓋既有檔案）',
    helper: '⚠️ 包含已就緒的 FAQ，會用新合成的語音覆蓋現有檔案',
    count: (s) => Object.values(s || {}).reduce((a, b) => a + (b || 0), 0),
  },
];

const FaqBulkRegenAudioDialog = ({ open, onClose, onSuccess, onError }) => {
  const [filter, setFilter] = useState('missing');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [statusSummary, setStatusSummary] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Refresh the count summary every time the dialog opens — the FaqPage row
  // statuses can change behind the dialog (worker emits or other tabs).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatusLoading(true);
    setStatusSummary(null);
    faqService
      .getAudioStatus()
      .then((res) => {
        if (cancelled) return;
        const byStatus = res?.data?.byStatus || res?.byStatus || {};
        setStatusSummary(byStatus);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('讀取語音狀態失敗:', err.message);
        setStatusSummary({});
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = () => {
    setFilter('missing');
    setError('');
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose && onClose();
  };

  const selectedCount = statusSummary
    ? FILTER_OPTIONS.find((o) => o.value === filter).count(statusSummary)
    : null;
  const readyCount = statusSummary?.ready || 0;

  const handleSubmit = async () => {
    setError('');
    if (filter === 'all') {
      const ok = window.confirm(
        `將重新產生 ${selectedCount ?? '全部'} 個 FAQ 的語音檔（包含 ${readyCount} 個已就緒的），會覆蓋既有檔案。確定要繼續嗎？`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const res = await faqService.regenerateAllAudio({ filter });
      const data = res?.data || {};
      const queued = data.queuedCount ?? 0;
      const eta = data.estimatedTotalSeconds ?? 0;
      onSuccess && onSuccess(
        queued > 0
          ? `已排入 ${queued} 個 FAQ 的語音產生工作（預估 ${Math.ceil(eta / 60)} 分鐘）`
          : '目前沒有符合條件的 FAQ'
      );
      reset();
      onClose && onClose();
    } catch (err) {
      const msg = err.message || '批次重新合成失敗';
      setError(msg);
      onError && onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>批次產生 FAQ 語音</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          選擇要重新產生語音的範圍。背景處理過程中，可繼續使用其他功能；
          每筆 FAQ 完成後表格會自動更新狀態。
        </Typography>

        {/* Status summary chips — shows current company-wide audio state */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {statusLoading && <Chip label="讀取中..." size="small" sx={NEUTRAL_CHIP_SX} />}
          {!statusLoading && statusSummary && (
            <>
              <Chip label={`已就緒 ${statusSummary.ready || 0}`} size="small" sx={NEUTRAL_CHIP_SX} />
              <Chip label={`需更新 ${statusSummary.stale || 0}`} size="small" sx={NEUTRAL_CHIP_SX} />
              <Chip label={`失敗 ${statusSummary.failed || 0}`} size="small" sx={NEUTRAL_CHIP_SX} />
              <Chip label={`從未產生 ${statusSummary.missing || 0}`} size="small" sx={NEUTRAL_CHIP_SX} />
              {(statusSummary.pending || 0) + (statusSummary.generating || 0) > 0 && (
                <Chip
                  label={`處理中 ${(statusSummary.pending || 0) + (statusSummary.generating || 0)}`}
                  size="small"
                  sx={NEUTRAL_CHIP_SX}
                />
              )}
            </>
          )}
        </Box>

        <RadioGroup value={filter} onChange={(e) => setFilter(e.target.value)}>
          {FILTER_OPTIONS.map((opt) => {
            const count = statusSummary ? opt.count(statusSummary) : null;
            return (
              <Box
                key={opt.value}
                sx={{
                  p: 1.25, mb: 1,
                  border: '1px solid',
                  borderColor: filter === opt.value ? 'primary.main' : 'rgba(0,0,0,0.12)',
                  borderRadius: 1,
                  bgcolor: filter === opt.value ? 'rgba(25,118,210,0.04)' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => setFilter(opt.value)}
              >
                <FormControlLabel
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{opt.label}</Typography>
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          {opt.helper}
                        </Typography>
                      </Box>
                      {count !== null && (
                        <Chip
                          label={`${count} 筆`}
                          size="small"
                          sx={{
                            ...NEUTRAL_CHIP_SX,
                            // Highlight zero-count options so admin sees them as no-ops
                            color: count === 0 ? 'rgba(0,0,0,0.38)' : NEUTRAL_CHIP_SX.color,
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                />
              </Box>
            );
          })}
        </RadioGroup>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>取消</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || selectedCount === 0}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {submitting
            ? '排入中...'
            : selectedCount === 0
              ? '無項目可產生'
              : `開始產生${selectedCount !== null ? `（${selectedCount} 筆）` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaqBulkRegenAudioDialog;
